import { useState, useEffect } from 'react';
import { X, Save, Trash2 } from 'lucide-react';
import { notasApi } from '../api/notas';
import { serviciosApi } from '../api/endpoints';

function fmtDateTime(d) {
  if (!d) return '';
  const date = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function ModalCrearNota({ isOpen, onClose, onSaved, clienteId, clienteNombre, empleados = [], servicios = [] }) {
  const [modo, setModo] = useState('existente');
  const [servicioId, setServicioId] = useState('');
  const [nombreServicio, setNombreServicio] = useState('');
  const [categoria, setCategoria] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [importe, setImporte] = useState(0);
  const [descuentoPct, setDescuentoPct] = useState(0);
  const [empleadoId, setEmpleadoId] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 16));
  const [crearEnCatalogo, setCrearEnCatalogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      // reset
      setModo('existente');
      setServicioId('');
      setNombreServicio('');
      setCategoria('');
      setDescripcion('');
      setImporte(0);
      setDescuentoPct(0);
      setEmpleadoId('');
      setCrearEnCatalogo(false);
      setError(null);
    } else {
      setFecha(new Date().toISOString().slice(0, 16));
    }
  }, [isOpen]);

  useEffect(() => {
    if (modo === 'existente') {
      setCrearEnCatalogo(false);
      setNombreServicio('');
      setCategoria('');
    }
  }, [modo]);

  if (!isOpen) return null;

  const isNuevo = modo === 'nuevo-nota' || modo === 'nuevo-catalogo';
  const esCrearEnCatalogo = modo === 'nuevo-catalogo';

  const validate = () => {
    if (!descripcion.trim()) return 'La descripción es obligatoria.';
    if (importe < 0) return 'El importe no puede ser negativo.';
    if (descuentoPct < 0 || descuentoPct > 100) return 'El descuento debe estar entre 0 y 100.';
    if (modo === 'existente' && !servicioId) return 'Selecciona un servicio del catálogo.';
    if (isNuevo) {
      if (!nombreServicio.trim()) return 'Indica el nombre del servicio nuevo.';
      if (!categoria.trim()) return 'Indica la categoría del servicio nuevo.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }

    setSaving(true);
    setError(null);
    try {
      const body = {
        fecha: new Date(fecha).toISOString(),
        empleadoId: empleadoId ? Number(empleadoId) : null,
        servicioId: modo === 'existente' ? Number(servicioId) : null,
        nombreServicio: isNuevo ? nombreServicio.trim() : null,
        categoria: isNuevo ? categoria.trim() : null,
        crearEnCatalogo: esCrearEnCatalogo,
        descripcion: descripcion.trim(),
        importe: Number(importe),
        descuentoPct: descuentoPct > 0 ? Number(descuentoPct) : null,
      };
      await notasApi.crear(clienteId, body);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar la nota');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-panel border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-bold text-white">Nueva nota de soporte</h2>
            <p className="text-xs text-muted mt-0.5">Cliente: {clienteNombre}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded text-slate-400 hover:text-white hover:bg-panel-2">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Fecha y hora</label>
              <input
                type="datetime-local"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Empleado atendido (opcional)</label>
              <select
                value={empleadoId}
                onChange={(e) => setEmpleadoId(e.target.value)}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              >
                <option value="">(sin asignar)</option>
                {empleados.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.nombreCompleto}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Servicio</label>
            <div className="flex flex-col gap-1.5 mb-2">
              {[
                { v: 'existente', l: 'Servicio existente del catálogo' },
                { v: 'nuevo-nota', l: 'Servicio nuevo (solo en la nota)' },
                { v: 'nuevo-catalogo', l: 'Servicio nuevo (también en catálogo)' },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-2 text-sm text-slate-200">
                  <input
                    type="radio"
                    name="modo"
                    value={opt.v}
                    checked={modo === opt.v}
                    onChange={() => setModo(opt.v)}
                    className="accent-cyan-500"
                  />
                  {opt.l}
                </label>
              ))}
            </div>

            {modo === 'existente' && (
              <select
                value={servicioId}
                onChange={(e) => setServicioId(e.target.value)}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
                required
              >
                <option value="">-- Selecciona un servicio --</option>
                {servicios.map((s) => (
                  <option key={s.id} value={s.id}>
                    [{s.codigo}] {s.nombre} {s.categoria ? `(${s.categoria})` : ''}
                  </option>
                ))}
              </select>
            )}

            {isNuevo && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Nombre del servicio *"
                  value={nombreServicio}
                  onChange={(e) => setNombreServicio(e.target.value)}
                  className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
                  required
                />
                <input
                  type="text"
                  placeholder="Categoría *"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
                  required
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Descripción *</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white resize-y"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Importe ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={importe}
                onChange={(e) => setImporte(Number(e.target.value))}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Descuento (% opcional)</label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={descuentoPct}
                onChange={(e) => setDescuentoPct(Number(e.target.value))}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              />
            </div>
          </div>

          {importe > 0 && descuentoPct > 0 && (
            <div className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2">
              Importe efectivo: <strong>${(importe * (1 - descuentoPct / 100)).toFixed(2)}</strong>
              {' '}(con {descuentoPct}% de descuento)
            </div>
          )}

          {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> {saving ? 'Guardando...' : 'Guardar nota'}
          </button>
        </div>
      </div>
    </div>
  );
}
